import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { accentBar, brandAlpha } from '../../../theme/mixins';
import { DARK_INK } from '../../../theme/brandTokens';

export const sectionStyles: SxProps<Theme> = {
    mt: 3,
};

export const cardStyles: SxProps<Theme> = {
    p: 3,
    mb: 3,
};

export const cardTitleStyles: SxProps<Theme> = {
    fontWeight: 600,
    mb: 0.5,
};

export const cardHintStyles: SxProps<Theme> = {
    color: 'text.secondary',
    mb: 2.5,
};

export const colourRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 3,
};

export const colourFieldStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    minWidth: 260,
};

/**
 * The native colour input, sized to match the text field beside it.
 *
 * `appearance: none` plus the swatch selectors below strip the chrome the browser puts
 * around the well, which otherwise renders as a thick grey frame that does not match
 * anything else on the page.
 */
export const colourSwatchStyles: SxProps<Theme> = {
    appearance: 'none',
    WebkitAppearance: 'none',
    width: 56,
    height: 56,
    padding: 0,
    border: '1px solid rgba(16,24,40,0.15)',
    borderRadius: '12px',
    background: 'none',
    cursor: 'pointer',
    flexShrink: 0,
    '&::-webkit-color-swatch-wrapper': { padding: 0 },
    '&::-webkit-color-swatch': { border: 'none', borderRadius: '11px' },
    '&::-moz-color-swatch': { border: 'none', borderRadius: '11px' },
};

export const hexFieldStyles: SxProps<Theme> = {
    width: 180,
};

export const warningStyles: SxProps<Theme> = {
    mt: 2,
};

// --- preview ---------------------------------------------------------------

/**
 * The preview surface.
 *
 * Deliberately on `background.default` rather than paper: these colours are judged against
 * the app's own backdrop, and a swatch on white flatters a pale brand that would disappear
 * on the real page.
 */
export const previewSurfaceStyles: SxProps<Theme> = {
    p: 3,
    borderRadius: '16px',
    backgroundColor: 'background.default',
    border: '1px solid rgba(16,24,40,0.08)',
};

export const previewRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 2,
};

export const previewCardStyles: SxProps<Theme> = {
    mt: 2.5,
    borderRadius: '16px',
    overflow: 'hidden',
    backgroundColor: 'background.paper',
    border: '1px solid rgba(16,24,40,0.06)',
    maxWidth: 320,
};

export const previewMastheadLabelStyles: SxProps<Theme> = {
    display: 'block',
    mb: 1,
    color: 'text.secondary',
};

/**
 * The email masthead, in miniature.
 *
 * The one surface a clinic's branding reaches that nobody here can otherwise see, so it has to
 * be honest about what a patient receives. The clinic's colour is a rule above and below and
 * the logo stands on the email's own white between them -- because a mark drawn for the app's
 * light surfaces does not survive a saturated background, and standing it on one lost Motion
 * Care's wordmark entirely. Mirrors the masthead in
 * `appointments/src/main/resources/brevo/transactional-template.html`; if the rules change
 * there, they change here.
 *
 * **White regardless of the admin's theme.** An email has no dark mode -- the template paints
 * #FFFFFF -- so this does not take `background.paper`, which would show a preview no patient
 * will ever get.
 *
 * The rules' colour is not in this object: it is the draft the admin is typing, so it arrives
 * inline from the component.
 */
export const previewMastheadStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 72,
    px: 2,
    py: 1.75,
    mb: 2.5,
    backgroundColor: '#FFFFFF',
    borderTop: '4px solid',
    borderBottom: '4px solid',
};

export const previewMastheadLogoStyles: SxProps<Theme> = {
    display: 'block',
    maxHeight: 32,
    maxWidth: 200,
    objectFit: 'contain',
};

/**
 * The template's own fallback: the clinic's name.
 *
 * In the ink and not in the clinic's colour, because this sits on white and a clinic is free
 * to pick a pale one -- Motion Care's own #EDC93B is 1.61:1 against white. `contrastTextFor`
 * cannot answer it either, since it says what reads *on* the brand colour.
 */
export const previewMastheadNameStyles: SxProps<Theme> = {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '0.02em',
    color: DARK_INK,
};

export const previewAccentBarStyles: SxProps<Theme> = accentBar;

export const previewCardBodyStyles: SxProps<Theme> = {
    p: 2.5,
};

export const previewEyebrowStyles: SxProps<Theme> = {
    display: 'inline-flex',
    px: 1.5,
    py: 0.4,
    mb: 1.5,
    borderRadius: 999,
    backgroundColor: brandAlpha('primary', 0.1),
    color: 'var(--ch-primary)',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
};

// --- logo ------------------------------------------------------------------

export const logoRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 3,
};

/**
 * Chequerboard behind the logo, so an image with no transparency is obvious before it is
 * discovered sitting as a white slab on the frosted header.
 */
export const logoPreviewStyles: SxProps<Theme> = {
    width: 220,
    height: 76,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 1,
    borderRadius: '12px',
    border: '1px solid rgba(16,24,40,0.12)',
    backgroundColor: '#fff',
    backgroundImage:
        'linear-gradient(45deg, #eef0f3 25%, transparent 25%), linear-gradient(-45deg, #eef0f3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eef0f3 75%), linear-gradient(-45deg, transparent 75%, #eef0f3 75%)',
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
};

export const logoImageStyles: SxProps<Theme> = {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
};

export const logoActionsStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 1,
};

// --- favicon ---------------------------------------------------------------

/**
 * Square rather than the logo's 220x76, because that is the shape of the thing.
 *
 * Same chequerboard and same reason -- transparency matters more in a browser tab than
 * anywhere else in the app, since the tab is whatever colour the reader's theme makes it.
 * The box is deliberately much bigger than the 16px a tab draws: this one is for judging the
 * crop, and the crop dialog's own preview is where it is seen at its real size.
 */
export const faviconPreviewStyles: SxProps<Theme> = {
    width: 76,
    height: 76,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 1,
    borderRadius: '12px',
    border: '1px solid rgba(16,24,40,0.12)',
    backgroundColor: '#fff',
    backgroundImage:
        'linear-gradient(45deg, #eef0f3 25%, transparent 25%), linear-gradient(-45deg, #eef0f3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eef0f3 75%), linear-gradient(-45deg, transparent 75%, #eef0f3 75%)',
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
};

// --- document watermark ----------------------------------------------------

export const watermarkRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 3,
};

/**
 * A sheet of A4, at its own 210:297.
 *
 * The whole job of this box is to answer one question -- will the document still be
 * readable -- so it has to be a page rather than a swatch. `aspectRatio` rather than a fixed
 * height so the two stay locked together if the width ever changes; the width is fixed
 * because a page that reflowed with the viewport would show a different mark at every
 * breakpoint while the printed one never moves.
 */
export const watermarkPageStyles: SxProps<Theme> = {
    width: 200,
    aspectRatio: '210 / 297',
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
    borderRadius: '4px',
    border: '1px solid rgba(16,24,40,0.12)',
    boxShadow: '0 2px 12px rgba(16,24,40,0.12)',
    // Paper, never a theme surface. A watermark judged against a tinted or dark box is
    // judged against a page no printer will produce -- and unlike everything else on this
    // screen, this one is not showing the *app*.
    backgroundColor: '#fff',
};

/** The mark, sized and faded by the two sliders. Both come through `style` per render. */
export const watermarkMarkStyles: SxProps<Theme> = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxHeight: '100%',
    objectFit: 'contain',
};

/** The document's heading, standing in for "Fișă de evaluare" at the top of a real page. */
export const watermarkPageHeadingStyles: SxProps<Theme> = {
    position: 'absolute',
    top: 12,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: '0.04em',
    // The document's own heading colour, not the clinic's: the evaluation template does not
    // wear the brand palette, and a preview that did would be flattering rather than honest.
    color: '#3f6bb0',
};

/**
 * Lines of nothing where the document's text goes.
 *
 * Drawn as a gradient rather than as dummy words for two reasons: it must not read as copy
 * anybody should try to parse, and the real document is Romanian prose that would otherwise
 * need translating into a locale file to sit here.
 */
export const watermarkPageLinesStyles: SxProps<Theme> = {
    position: 'absolute',
    inset: '30px 16px 16px',
    backgroundImage:
        'repeating-linear-gradient(to bottom, rgba(16,24,40,0.45) 0 3px, transparent 3px 12px)',
    // A page of identical full-width bars reads as a barcode; stopping short leaves it
    // looking like text that ended.
    WebkitMaskImage: 'linear-gradient(to bottom, #000 0 84%, transparent 84%)',
    maskImage: 'linear-gradient(to bottom, #000 0 84%, transparent 84%)',
};

export const watermarkControlsStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 1,
    flex: '1 1 260px',
    // min(...) rather than a bare px floor: a hard one pushes the page sideways as soon as
    // the card is narrower than it.
    minWidth: 'min(260px, 100%)',
};

export const watermarkActionsStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    mb: 1,
};

export const watermarkSliderRowStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
};

export const watermarkSliderLabelStyles: SxProps<Theme> = {
    color: 'text.secondary',
    // Wide enough for the longer of the two labels in either language, so the two sliders
    // start at the same x and read as a pair rather than as a ragged list.
    minWidth: 92,
    flexShrink: 0,
};

export const watermarkSliderValueStyles: SxProps<Theme> = {
    color: 'text.secondary',
    minWidth: 44,
    textAlign: 'right',
    // Tabular figures, or the slider's own track appears to twitch sideways as the number
    // goes from 9 to 10.
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
};

export const watermarkPreviewLabelStyles: SxProps<Theme> = {
    color: 'text.secondary',
    display: 'block',
    mb: 0.5,
};

export const watermarkFallbackStyles: SxProps<Theme> = { color: 'text.secondary', mt: 1 };

// --- contact links ---------------------------------------------------------

/**
 * One field per row rather than a grid.
 *
 * These are URLs, and a two-column layout truncates a Google Maps share link to the point
 * where an admin cannot tell which one they pasted. The `min(Npx, 100%)` floor is the grid
 * rule from CLAUDE.md -- a bare 320px minimum pushes the page sideways on a phone.
 */
export const linkRowStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'minmax(min(320px, 100%), 1fr)',
    gap: 2.5,
};

export const linkFieldStyles: SxProps<Theme> = {
    maxWidth: 520,
};

export const actionsStyles: SxProps<Theme> = {
    display: 'flex',
    gap: 2,
    flexWrap: 'wrap',
};

/**
 * The default-language picker. Bounded rather than full width: it holds one of three short
 * words, and a select stretched across the card reads as a text field somebody forgot to
 * fill in.
 */
export const languageFieldStyles: SxProps<Theme> = {
    maxWidth: 260,
    mb: 1,
};
